// Totales del listado por código HS y correcciones manuales de línea.
//
// Mismo planteamiento que `packingList.ts` y OTRO GRANO: aquí la fila es un
// LOTE de un artículo, no un (matrícula × artículo). Por eso son dos ficheros y
// no uno con un parámetro: la clave de corrección, los campos editables y la
// cascada son distintos, y compartirlos obligaría a preguntarse en cada función
// «¿y esto para cuál de los dos documentos era?».
//
// El documento agrupa y totaliza por código HS y por país de origen, así que la
// pantalla lo agrupa igual: revisar una lista plana no diría si un código está
// bien repartido.
import type { HsCodeLinea } from '@/types'

/** Separador de las claves compuestas.
 *
 *  Es el caracter NUL: ningun codigo de articulo, matricula ni lote lo
 *  contiene, asi que dos claves distintas no pueden colisionar (un espacio
 *  si podria, si algun dia un codigo llevara uno).
 *
 *  Se construye con `fromCharCode` y no se escribe ni crudo ni como escape:
 *  un NUL literal en el fuente hace que git marque el fichero como binario y
 *  se quede sin diff ni blame. Esto es ASCII plano. */
const SEP = String.fromCharCode(0)

export interface TotalesHsCode {
  lineas: number
  bultos: number
  cantidad: number
  neto: number
  bruto: number
  importe: number
}

/** La fila de totales tal como saldrá en el PDF. */
export function totalesHsCode(lineas: HsCodeLinea[]): TotalesHsCode {
  return lineas.reduce<TotalesHsCode>(
    (a, l) => ({
      lineas: a.lineas + 1,
      bultos: a.bultos + (l.bultos ?? 0),
      cantidad: a.cantidad + (l.cantidad ?? 0),
      neto: a.neto + (l.pesoNeto ?? 0),
      bruto: a.bruto + (l.pesoBruto ?? 0),
      importe: a.importe + (l.precioNeto ?? 0),
    }),
    { lineas: 0, bultos: 0, cantidad: 0, neto: 0, bruto: 0, importe: 0 },
  )
}

/** Un grupo del documento: un código HS dentro de un país de origen. */
export interface GrupoHsCode {
  clave: string
  hscode: string
  origen: string
  lineas: HsCodeLinea[]
  totales: TotalesHsCode
}

/** Agrupa como el PDF: por (origen, código HS).
 *
 *  Se conserva el orden de aparición, que es el que trae el procedure
 *  (`ORDER BY origen, articulo, lote`). Reordenar aquí haría que la pantalla y
 *  el documento enseñaran los grupos en distinto orden.
 *
 *  Un código HS vacío forma su propio grupo en vez de repartirse: es el hueco
 *  que hay que ver, y esconderlo dentro de otro grupo lo taparía. */
export function agruparHsCode(lineas: HsCodeLinea[]): GrupoHsCode[] {
  const grupos = new Map<string, GrupoHsCode>()
  for (const l of lineas) {
    const clave = `${l.origen}${SEP}${l.hscode}`
    const g = grupos.get(clave)
    if (g) g.lineas.push(l)
    else grupos.set(clave, {
      clave, hscode: l.hscode, origen: l.origen, lineas: [l],
      totales: { lineas: 0, bultos: 0, cantidad: 0, neto: 0, bruto: 0, importe: 0 },
    })
  }
  for (const g of grupos.values()) g.totales = totalesHsCode(g.lineas)
  return [...grupos.values()]
}

// ── Correcciones manuales ────────────────────────────────────────────────────
//
// Hacen más falta aquí que en el packing: el código HS falta en un tercio de los
// artículos que pasan por cargas (medido: 66 % de cobertura en avi, 68 % en
// tdis), y es justo el dato que este documento existe para declarar.

/** Campos corregibles: los que el PDF imprime.
 *
 *  Las columnas de control (`nPreciosDistintos`, `unCajaOrigen`) quedan fuera:
 *  explican de dónde sale el dato, y editarlas haría que la explicación
 *  mintiera. */
export const CAMPOS_HS = [
  'descripcion', 'hscode', 'origen', 'cantidad', 'bultos',
  'precioUnitario', 'precioNeto', 'pesoNeto', 'pesoBruto',
] as const

export type CampoHs = typeof CAMPOS_HS[number]

/** Campos de TEXTO. El resto se convierte a número al teclearlos.
 *
 *  El código HS es texto aunque parezca un número: lleva ceros a la izquierda
 *  ('03051000') que un `number` se comería, y un código truncado en una aduana
 *  es peor que uno en blanco. */
const TEXTO: CampoHs[] = ['descripcion', 'hscode', 'origen']

/** Lo único que se calcula a partir de otro campo: el importe de la línea. */
export const CAMPOS_HS_DERIVADOS: CampoHs[] = ['precioNeto']

/** Identidad de una línea: **(artículo, lote)**.
 *
 *  NO es la del packing —(matrícula, artículo)— y no son intercambiables: un
 *  lote puede repartirse entre varios palets y un palet llevar varios lotes. */
export function claveHs(l: { articulo: string; lote?: string | null }): string {
  return `${l.articulo}${SEP}${l.lote ?? ''}`
}

export interface CorreccionHs {
  valores: Partial<Record<CampoHs, string | number>>
  /** Campos que NO se recalculan. Editar una celda la fija automáticamente. */
  fijados: CampoHs[]
  excluida: boolean
}

const dec2 = (n: number) => Math.round(n * 100) / 100

export function correccionHsVacia(c: CorreccionHs | undefined): boolean {
  if (!c) return true
  return !c.excluida && Object.keys(c.valores).length === 0
}

/** La línea con la corrección aplicada y la cascada resuelta.
 *
 *  Sin corrección devuelve la línea TAL CUAL: recalcular una intacta la movería
 *  un céntimo por el redondeo, y abrir el cajón no puede cambiar cifras que
 *  nadie ha tocado. */
export function aplicarCorreccionHs(
  original: HsCodeLinea,
  correccion: CorreccionHs | undefined,
): HsCodeLinea {
  if (correccionHsVacia(correccion)) return original

  const fijados = new Set(correccion!.fijados)
  const l: HsCodeLinea = { ...original }

  for (const [campo, valor] of Object.entries(correccion!.valores)) {
    const c = campo as CampoHs
    if (TEXTO.includes(c)) {
      ;(l as unknown as Record<string, unknown>)[c] = String(valor ?? '')
      continue
    }
    const texto = String(valor ?? '').trim().replace(',', '.')
    ;(l as unknown as Record<string, unknown>)[c] = texto === '' ? null : Number(texto)
  }

  // Importe = precio unitario × cantidad. Sólo si se conocen los dos: inventar
  // un cero en la casilla del valor de la mercancía sería peor que el hueco.
  if (!fijados.has('precioNeto') && l.precioUnitario != null && l.cantidad) {
    l.precioNeto = dec2(l.precioUnitario * l.cantidad)
  }

  return l
}

export function lineasHsFinales(
  originales: HsCodeLinea[],
  correcciones: Record<string, CorreccionHs>,
): HsCodeLinea[] {
  return originales
    .filter(l => !correcciones[claveHs(l)]?.excluida)
    .map(l => aplicarCorreccionHs(l, correcciones[claveHs(l)]))
}

/** ¿Qué campos difieren del original? Para marcarlos: un valor corregido a mano
 *  y uno del sistema son idénticos a la vista. */
export function camposHsCorregidos(
  original: HsCodeLinea,
  final: HsCodeLinea,
): Set<CampoHs> {
  const salida = new Set<CampoHs>()
  for (const campo of CAMPOS_HS) {
    const a = original[campo] as string | number | null | undefined
    const b = final[campo] as string | number | null | undefined
    if (a == null && b == null) continue
    if (typeof a === 'number' || typeof b === 'number') {
      if (Math.abs(Number(a ?? 0) - Number(b ?? 0)) >= 0.005) salida.add(campo)
    } else if (String(a ?? '') !== String(b ?? '')) {
      salida.add(campo)
    }
  }
  return salida
}

export interface CorreccionHsEnviada {
  articulo: string
  lote?: string | null
  excluida: boolean
  descripcion?: string | null
  hscode?: string | null
  origen?: string | null
  cantidad?: number | null
  bultos?: number | null
  precioUnitario?: number | null
  precioNeto?: number | null
  pesoNeto?: number | null
  pesoBruto?: number | null
}

/** Lo que se manda al backend: sólo lo corregido, con su clave completa.
 *
 *  Se comparan los valores FINALES contra el original, no lo que el usuario
 *  teclee: así viaja también lo que la cascada recalculó, y no viaja lo que
 *  volvió a su valor de origen. */
export function correccionesHsParaEnviar(
  originales: HsCodeLinea[],
  correcciones: Record<string, CorreccionHs>,
): CorreccionHsEnviada[] {
  const salida: CorreccionHsEnviada[] = []

  for (const original of originales) {
    const c = correcciones[claveHs(original)]
    if (!c) continue

    const base = { articulo: original.articulo, lote: original.lote ?? null }
    if (c.excluida) {
      salida.push({ ...base, excluida: true })
      continue
    }

    const final = aplicarCorreccionHs(original, c)
    const campos = camposHsCorregidos(original, final)
    if (campos.size === 0) continue

    const envio: CorreccionHsEnviada = { ...base, excluida: false }
    for (const campo of campos) {
      ;(envio as unknown as Record<string, unknown>)[campo] = final[campo] ?? null
    }
    salida.push(envio)
  }

  return salida
}

/** Campos que al corregirse se propagan a TODAS las líneas de esa referencia.
 *
 *  Son los del maestro de ARTÍCULO: el código HS, la descripción y el país de
 *  origen no cambian de un lote a otro. Teclear el código HS que falta una vez
 *  por lote sería el mismo trabajo repetido, y basta con que uno se olvide para
 *  que el documento declare dos códigos distintos para la misma referencia.
 *
 *  Las cantidades, los bultos y los pesos NO se propagan: son de ese lote. */
export function camposHsPropagables(): CampoHs[] {
  return ['descripcion', 'hscode', 'origen']
}

/** Las claves de todas las líneas de la misma referencia. */
export function clavesHsDelArticulo(lineas: HsCodeLinea[], articulo: string): string[] {
  return lineas.filter(l => l.articulo === articulo).map(claveHs)
}
